import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * iter83 — Edit-aware auto-refresh hook.
 *
 * Polls `fetcher` every `intervalMs` (default 20 000) but PAUSES while:
 *   - the user is typing in any input/textarea/contenteditable (document.activeElement)
 *   - a wizard/modal is open (caller passes `pausedByWizard` flag)
 *   - the tab is hidden
 *
 * Returns:
 *   { refresh, lastUpdated, isFetching, isPaused }
 *
 * Designed so callers retain ownership of their state — we never touch `data`
 * directly, we just trigger their `fetcher`. That means a fetcher whose component
 * uses functional setState patterns won't clobber typed/uncommitted UI state.
 */
export function useAutoRefresh(fetcher, { intervalMs = 20000, pausedByWizard = false } = {}) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const fetcherRef = useRef(fetcher);
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  const isEditing = () => {
    const el = document.activeElement;
    if (!el) return false;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (el.getAttribute && el.getAttribute('contenteditable') === 'true') return true;
    return false;
  };

  const refresh = useCallback(async (opts = {}) => {
    if (!fetcherRef.current) return;
    setIsFetching(true);
    try {
      await fetcherRef.current(opts);
      setLastUpdated(new Date());
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    if (intervalMs <= 0) return undefined;
    const tick = () => {
      const paused = pausedByWizard || isEditing() || document.visibilityState !== 'visible';
      setIsPaused(paused);
      if (!paused) refresh({ silent: true });
    };
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, pausedByWizard, refresh]);

  return { refresh, lastUpdated, isFetching, isPaused };
}

/**
 * Small UI helper — formats elapsed seconds since `lastUpdated`.
 * Re-renders every second via an internal interval.
 */
export function useTimeAgo(date) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!date) return 'never';
  const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}
